import type { InputHTMLAttributes, ReactNode, SelectHTMLAttributes } from 'react'
import { cx } from './cx'

/**
 * Everything a control shares except its horizontal padding, which differs: a
 * select has to leave room on the right for its own chevron, and mixing
 * `px-*` with `pr-*` leaves the winner to Tailwind's property ordering rather
 * than to anything stated here.
 */
const CONTROL_BASE =
  'w-full rounded-md border border-ink-300 bg-white py-2 text-sm text-ink-900 ' +
  'placeholder:text-ink-400 focus:border-brand-600 disabled:bg-ink-50 disabled:text-ink-500'

const CONTROL = `${CONTROL_BASE} px-3`

/**
 * Label, control and hint as one unit.
 *
 * The `id` is required rather than generated: an input whose label is not
 * actually associated with it is a form that cannot be filled in by anyone
 * using a screen reader, and generating ids makes that easy to leave broken.
 */
export function Field({
  id,
  label,
  hint,
  children,
  className,
}: {
  id: string
  label: ReactNode
  hint?: ReactNode
  children: ReactNode
  className?: string
}) {
  return (
    <div className={className}>
      <label htmlFor={id} className="block text-sm font-medium text-ink-800">
        {label}
      </label>
      {children}
      {hint && (
        <p id={`${id}-hint`} className="mt-1 text-xs text-ink-600">
          {hint}
        </p>
      )}
    </div>
  )
}

export function TextInput({ className, ...rest }: InputHTMLAttributes<HTMLInputElement>) {
  return <input className={cx(CONTROL, 'date-control mt-1', className)} {...rest} />
}

/**
 * A select that matches the text inputs beside it.
 *
 * Left to itself a `<select>` is drawn by the platform: on Windows a squared,
 * heavier control with its own arrow, sitting a pixel or two off the inputs it
 * shares a row with. `appearance-none` gives that up in exchange for having to
 * draw the arrow ourselves — see `.select-control` — and the padding on the
 * right is what keeps a long option from running underneath it.
 */
export function Select({ className, ...rest }: SelectHTMLAttributes<HTMLSelectElement>) {
  return (
    <select
      className={cx(
        CONTROL_BASE,
        'select-control mt-1 cursor-pointer appearance-none pl-3 pr-9 disabled:cursor-default',
        className
      )}
      {...rest}
    />
  )
}
